import { Button, Flex, FormControl, FormLabel, Input, Textarea, FormErrorMessage, FormHelperText, Box } from "@chakra-ui/react";
import { MultiSelectControlled, SingleSelectControlled } from "am-tax-fe-core";
import { FunctionComponent, useMemo, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { CreateEntity, Entity, UpdateEntity } from "../../../api/entities/entityApi";
import { useCreateEntity, useDeleteEntity, useUpdateEntity } from "../../../api/entities/entityQueries";
import { Jurisdiction } from "../../../api/jurisdictions/jurisdictionApi";
import { MAX_HEADCOUNT } from "../../../utils/constants";
import { ConfirmModal } from "./ConfirmModal";

interface EntityFormProps {
    show: boolean;
    onClose: () => void;
    rowData?: Entity;
    jurisdictions: Jurisdiction[];
    onSave?: (data: Entity) => void;
    clientId?: string;
}

const CHARACTERIZATION_OPTIONS = ["Service Recipient", "Licensor", "Principal", "Service Provider", "Licensee", "Distributor"];

type FormFields = {
    legalName: string;
    abbreviatedName: string;
    operatingJurisdiction: string;
    description: string;
    headcount: string;
    characterization: string[];
    yearEnded: string;
};

export const EntityForm: FunctionComponent<EntityFormProps> = ({ onClose, rowData, jurisdictions, clientId }) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const formValues: FormFields = {
        legalName: rowData?.fullLegalName || "",
        abbreviatedName: rowData?.abbreviatedName || "",
        operatingJurisdiction: rowData?.operatingJurisdiction || "",
        description: rowData?.description || "",
        headcount: rowData?.headcount || "",
        characterization: rowData?.characterization ? rowData.characterization.split(",") : [],
        yearEnded: formatDate(rowData?.yearEnded) || formatDate(new Date().toString()),
    };

    const isEdit: boolean = !!rowData;

    const {
        control,
        handleSubmit,
        register,
        formState: { errors },
    } = useForm<FormFields>({ values: formValues });

    const { mutateAsync: updateEntityMutation, isPending: isUpdatingEntity } = useUpdateEntity();
    const { mutateAsync: createEntityMutation, isPending: isCreatingEntity } = useCreateEntity();
    const { mutateAsync: deleteEntityMutation, isPending: isDeletingEntity } = useDeleteEntity();
    const isSavingEntity = isUpdatingEntity || isCreatingEntity;

    const sortedJurisdictions = useMemo(() => {
        return [...jurisdictions].sort((a, b) => a.name.localeCompare(b.name)).map(j => ({ value: j.id, label: j.name }));
    }, [jurisdictions]);

    // TODO: Add error handling for all of these mutations
    const onSubmit: SubmitHandler<FormFields> = async data => {
        const entityId = rowData?.id;
        if (isEdit) {
            const entityArgs: UpdateEntity = {
                id: entityId!,
                fullLegalName: data.legalName,
                abbreviatedName: data.abbreviatedName,
                operatingJurisdiction: data.operatingJurisdiction,
                description: data.description,
                headcount: data.headcount,
                characterization: data.characterization.join(","),
                clientId: clientId || "",
                yearEnded: data.yearEnded || new Date().toISOString(),
            };
            await updateEntityMutation(entityArgs);
        } else {
            const entityArgs: CreateEntity = {
                fullLegalName: data.legalName,
                abbreviatedName: data.abbreviatedName,
                operatingJurisdiction: data.operatingJurisdiction,
                description: data.description,
                headcount: data.headcount,
                characterization: data.characterization.join(","),
                clientId: clientId || "",
                yearEnded: data.yearEnded || new Date().toISOString(),
            };
            await createEntityMutation(entityArgs);
        }
        onClose();
    };

    const handleDelete = async () => {
        if (rowData) {
            await deleteEntityMutation({ id: rowData.id, clientId: clientId || "" });
            onClose();
        }
    };

    const closeConfirmModal = () => {
        setShowConfirmModal(false);
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Flex gap="2rem" flexFlow="column nowrap">
                    <Flex flexDirection="column" gap={4}>
                        <FormControl isRequired isInvalid={!!errors.legalName}>
                            <FormLabel>Full Legal Name</FormLabel>
                            <Input {...register("legalName", { required: "Legal name is required" })} placeholder="Full legal name" />
                            <FormErrorMessage>{errors.legalName?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.abbreviatedName}>
                            <FormLabel>Abbreviated Name</FormLabel>
                            <Input {...register("abbreviatedName", { required: "Abbreviated name is required" })} placeholder="Abbreviated name" />
                            <FormErrorMessage>{errors.abbreviatedName?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.operatingJurisdiction}>
                            <FormLabel>Operating Jurisdiction</FormLabel>
                            <Box>
                                <SingleSelectControlled
                                    control={control}
                                    options={sortedJurisdictions}
                                    name={"operatingJurisdiction"}
                                    rules={{ required: "Operating jurisdiction is required" }}
                                />
                            </Box>
                            <FormErrorMessage>{errors.operatingJurisdiction?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.description}>
                            <FormLabel>Brief Entity Description</FormLabel>
                            <Textarea {...register("description", { required: "Description is required" })} placeholder="Entity description..." />
                            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.headcount}>
                            <FormLabel>Headcount</FormLabel>
                            <Input
                                {...register("headcount", {
                                    required: "Headcount is required",
                                    max: {
                                        value: MAX_HEADCOUNT,
                                        message: "Headcount cannot exceed the current human population (~8,000,000,000)",
                                    },
                                })}
                                placeholder="e.g. 500"
                                type="number"
                                min={1}
                                max={MAX_HEADCOUNT}
                            />
                            <FormErrorMessage>{errors.headcount?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.yearEnded}>
                            <FormLabel>Year End</FormLabel>
                            <Input type="date" {...register("yearEnded", { required: "Year end is required" })} placeholder="Select year end" />
                            <FormErrorMessage>{errors.yearEnded?.message as string}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.characterization}>
                            <FormLabel>Characterization</FormLabel>
                            <MultiSelectControlled
                                control={control}
                                options={CHARACTERIZATION_OPTIONS.map(opt => ({ value: opt, label: opt }))}
                                name={"characterization"}
                                rules={{ required: "Characterization is required" }}
                            />
                            <FormHelperText>Select one or more underlying characterization</FormHelperText>
                            <FormErrorMessage>{errors.characterization?.message as string}</FormErrorMessage>
                        </FormControl>
                    </Flex>
                </Flex>
                <Flex gap="2rem" flexFlow="column nowrap">
                    <Flex w="100%" justify="space-between" align="center">
                        {isEdit ? (
                            <Button
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => setShowConfirmModal(true)}
                                isLoading={isDeletingEntity}
                                isDisabled={isDeletingEntity}
                            >
                                Delete Entity
                            </Button>
                        ) : (
                            <span />
                        )}
                        <Flex gap={2}>
                            <Button onClick={onClose} mr={3} variant="ghost">
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" isLoading={isSavingEntity} isDisabled={isSavingEntity}>
                                {rowData ? "Update" : "Add"}
                            </Button>
                        </Flex>
                    </Flex>
                </Flex>
            </form>
            <ConfirmModal show={showConfirmModal} handleDelete={handleDelete} onClose={closeConfirmModal} isDeletingEntity={isDeletingEntity} />
        </>
    );
};
